"use client";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

import { radius } from "../tokens/radius";

export type CardGridSkeletonProps = {
  /**
   * `cards` — grille responsive de blocs hauts, 1 / 2 / 3 colonnes.
   * `list` — pile de lignes basses.
   *
   * Doit refléter la vue que l'utilisateur a choisie : un skeleton dont la
   * forme ne ressemble pas au contenu à venir provoque un saut de mise en page
   * à l'arrivée des données, ce qui est plus désagréable qu'un simple délai.
   */
  view: "cards" | "list";
  /** Nombre de blocs. Défaut : 6 en grille, 4 en liste. */
  count?: number;
  "data-testid"?: string;
};

const DEFAULT_COUNT = { cards: 6, list: 4 } as const;

/**
 * Chaque bloc le porte, indépendamment du `data-testid` du conteneur : c'est ce
 * qui permet d'en compter le nombre sans descendre dans le DOM.
 */
const BLOCK_TESTID = "card-grid-skeleton-block";

/**
 * Squelette d'une liste en cours de chargement, dans les deux vues que les
 * pages de catalogue proposent.
 *
 * Il remplace deux fonctions identiques au nom près — `ProductsSkeleton` et
 * `ItinerariesSkeleton` — dans deux pages qui offrent le même basculement
 * cards / liste.
 *
 * Les hauteurs (240 et 84) et les rayons (`lg` et `md`) sont ceux des cards
 * réelles : c'est la seule chose qui rend un skeleton utile plutôt que
 * décoratif.
 */
export function CardGridSkeleton({
  view,
  count,
  "data-testid": dataTestId,
}: CardGridSkeletonProps) {
  const blocks = Array.from(
    { length: count ?? DEFAULT_COUNT[view] },
    (_, index) => index,
  );

  if (view === "list") {
    return (
      <Stack spacing={1} data-testid={dataTestId}>
        {blocks.map((index) => (
          <Skeleton
            key={index}
            data-testid={BLOCK_TESTID}
            variant="rectangular"
            height={84}
            sx={{ borderRadius: radius.md }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
        },
        gap: 2,
      }}
    >
      {blocks.map((index) => (
        <Skeleton
          key={index}
          data-testid={BLOCK_TESTID}
          variant="rectangular"
          height={240}
          sx={{ borderRadius: radius.lg }}
        />
      ))}
    </Box>
  );
}
